import * as ToastPrimitive from '@radix-ui/react-toast';
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cn } from '../lib/cn';
import { focusRing } from '../lib/focus-ring';

/** Status colour is semantic: `success` and `error` mean exactly that. */
export type ToastTone = 'default' | 'success' | 'error';

export interface ToastOptions {
  title: string;
  description?: string;
  tone?: ToastTone;
  /** Milliseconds before auto-dismiss. Falls back to the provider's default. */
  duration?: number;
}

interface ToastItem extends ToastOptions {
  id: string;
  open: boolean;
}

export interface ToastContextValue {
  /** Queues a toast and returns its id. */
  toast: (options: ToastOptions) => string;
  /** Dismisses a toast early. */
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/** Access the toaster. Throws outside `<ToastProvider>` so the bug is loud. */
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (context === null) {
    throw new Error('useToast() must be used inside a <ToastProvider>.');
  }
  return context;
}

const toneStyles: Record<ToastTone, { root: string; icon: string; Icon: typeof Info }> = {
  default: { root: 'border-line-strong', icon: 'text-info', Icon: Info },
  success: { root: 'border-good/35', icon: 'text-good', Icon: CircleCheck },
  error: { root: 'border-bad/45', icon: 'text-bad', Icon: CircleAlert },
};

/** Time the exit animation is given before the node is unmounted. */
const EXIT_MS = 180;

let toastCounter = 0;

export interface ToastProviderProps {
  children: ReactNode;
  /** Default auto-dismiss, in milliseconds. */
  duration?: number;
}

/**
 * Wrap the app once. Form feedback then goes through `useToast()`:
 *
 *     const { toast } = useToast();
 *     toast({ tone: 'success', title: 'Tenant created' });
 */
export function ToastProvider({ children, duration = 5000 }: ToastProviderProps) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef(new Map<string, number>());

  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach((timer) => {
        window.clearTimeout(timer);
      });
      pending.clear();
    };
  }, []);

  const dismiss = useCallback((id: string) => {
    setItems((current) =>
      current.map((item) => (item.id === id ? { ...item, open: false } : item)),
    );

    if (timers.current.has(id)) return;

    const timer = window.setTimeout(() => {
      timers.current.delete(id);
      setItems((current) => current.filter((item) => item.id !== id));
    }, EXIT_MS);

    timers.current.set(id, timer);
  }, []);

  const toast = useCallback((options: ToastOptions) => {
    toastCounter += 1;
    const id = `wend-toast-${toastCounter}`;
    setItems((current) => [...current, { ...options, id, open: true }]);
    return id;
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider swipeDirection="right" duration={duration}>
        {children}

        {items.map((item) => {
          const tone = toneStyles[item.tone ?? 'default'];
          const { Icon } = tone;

          return (
            <ToastPrimitive.Root
              key={item.id}
              open={item.open}
              duration={item.duration}
              onOpenChange={(next) => {
                if (!next) dismiss(item.id);
              }}
              className={cn(
                'flex items-start gap-3 rounded-base border bg-surface-2/95 p-3.5 shadow-lift backdrop-blur-xl',
                'data-[state=open]:animate-toast-in data-[state=closed]:animate-toast-out',
                'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]',
                'data-[swipe=cancel]:translate-x-0 data-[swipe=end]:animate-toast-out',
                tone.root,
              )}
            >
              <Icon aria-hidden="true" className={cn('mt-0.5 size-4 shrink-0', tone.icon)} />

              <div className="min-w-0 flex-1">
                <ToastPrimitive.Title
                  className={cn('font-mono text-[0.72rem] uppercase tracking-[0.12em]', tone.icon)}
                >
                  {item.title}
                </ToastPrimitive.Title>
                {item.description && (
                  <ToastPrimitive.Description className="mt-1 text-[0.86rem] leading-snug text-muted">
                    {item.description}
                  </ToastPrimitive.Description>
                )}
              </div>

              <ToastPrimitive.Close
                aria-label="Dismiss"
                className={cn(
                  'cursor-pointer rounded-sm p-1 text-muted transition-colors hover:text-text',
                  focusRing,
                )}
              >
                <X className="size-3.5" aria-hidden="true" />
              </ToastPrimitive.Close>
            </ToastPrimitive.Root>
          );
        })}

        <ToastPrimitive.Viewport className="fixed bottom-0 right-0 z-50 m-0 flex w-[min(24rem,100vw-1.5rem)] list-none flex-col gap-2 p-4 outline-none" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}
